import React, { FC, Fragment, useCallback } from 'react';
import { FlatList, View, StyleSheet } from 'react-native';

import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import { ContainerStyle } from '@interfaces/base';
import { DashboardScope, DashboardResultType, DashboardResultOrder } from '@api/domain/types';
import type { DashboardResult } from '@api/domain/types';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

import { getDashboardResultId } from '@v2/pages/dashboard/common/helpers';
import { DashboardListMode } from '@v2/pages/dashboard/common/types/list';

import DashboardListActualRow from './components/DashboardListActualRow';
import DashboardListDivergingRow from './components/DashboardListDivergingRow';
import DashboardListSkeletonRow from './components/DashboardListSkeletonRow';
import DashboardListEmpty from './components/DashboardListEmpty';
import DashboardListError from './components/DashboardListError';
import { useManageDashboardList } from './hooks/useManageDashboardList';
import { FOOTER_SKELETON_COUNT, INITIAL_SKELETON_COUNT } from './constants';
import { getBoxShadowStyle } from '@styles/shadow';

export interface DashboardListProps extends WithColor, ContainerStyle {
  scope: DashboardScope;
  resultType: DashboardResultType;
  orderBy: DashboardResultOrder;
  from: string;
  to: string;
  items: DashboardResult[];
  loading: boolean;
  error: string | null;
  pageNumber: number;
  onLoadMore: () => void;
  onRetry: () => void;
  onRowPress?: (item: DashboardResult) => void;
  testID?: string;
  rowTestIDForId?: (id: string) => string | undefined;
  emptyTestID?: string;
  errorTestID?: string;
  retryTestID?: string;
}

const DashboardList: FC<DashboardListProps> = ({
  C,
  scope,
  resultType,
  orderBy,
  from,
  to,
  items,
  loading,
  error,
  pageNumber,
  onLoadMore,
  onRetry,
  onRowPress,
  containerStyle,
  testID,
  rowTestIDForId,
  emptyTestID,
  errorTestID,
  retryTestID,
}) => {
  const resetSignature = `${scope}|${resultType}|${orderBy}|${from}|${to}`;
  const { mode, maxSeconds, maxPositive, maxNegative } = useManageDashboardList({
    items,
    resultType,
    pageNumber,
    resetSignature,
  });
  const boxShadowStyles = getBoxShadowStyle({ type: 'Narrow', C });

  const keyExtractor = useCallback((item: DashboardResult) => `${item.$type}:${getDashboardResultId(item)}`, []);

  const renderItem = useCallback(
    ({ item, index }: { item: DashboardResult; index: number }) => {
      const borderRadiusConfig = { top: index === 0, bottom: index === items.length - 1 };
      const itemId = getDashboardResultId(item);
      const rowTestID = rowTestIDForId?.(itemId);
      const onPress = onRowPress ? () => onRowPress(item) : () => {};
      if (mode === DashboardListMode.Actual) {
        return (
          <DashboardListActualRow
            scope={scope}
            item={item}
            maxSeconds={maxSeconds}
            borderRadiusConfig={borderRadiusConfig}
            onPress={onPress}
            testID={rowTestID}
          />
        );
      }
      return (
        <DashboardListDivergingRow
          scope={scope}
          item={item}
          maxPositive={maxPositive}
          maxNegative={maxNegative}
          borderRadiusConfig={borderRadiusConfig}
          onPress={onPress}
          testID={rowTestID}
        />
      );
    },
    [mode, scope, maxSeconds, maxPositive, maxNegative, items.length, rowTestIDForId, onRowPress]
  );

  const renderSeparator = useCallback(
    () => <View style={{ height: StyleSheet.hairlineWidth, backgroundColor: C('Hairline/Light/No trans') }} />,
    [C]
  );

  if (error && items.length === 0) {
    return (
      <View style={containerStyle}>
        <DashboardListError onPress={onRetry} message={error} testID={errorTestID} retryTestID={retryTestID} />
      </View>
    );
  }

  if (loading && items.length === 0) {
    return (
      <View style={containerStyle}>
        {Array.from({ length: INITIAL_SKELETON_COUNT }).map((_, i) => (
          <Fragment key={i}>
            <DashboardListSkeletonRow scope={scope} borderRadiusConfig={{ top: i === 0, bottom: i === INITIAL_SKELETON_COUNT - 1 }} />
            {i < INITIAL_SKELETON_COUNT - 1 && renderSeparator()}
          </Fragment>
        ))}
      </View>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <View style={containerStyle}>
        <DashboardListEmpty testID={emptyTestID} />
      </View>
    );
  }

  return (
    <FlatList
      testID={testID}
      showsVerticalScrollIndicator={false}
      data={items}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      ItemSeparatorComponent={renderSeparator}
      onEndReached={onLoadMore}
      onEndReachedThreshold={0.2}
      ListFooterComponent={
        loading ? (
          <View>
            {Array.from({ length: FOOTER_SKELETON_COUNT }).map((_, i) => (
              <Fragment key={i}>
                {renderSeparator()}
                <DashboardListSkeletonRow scope={scope} borderRadiusConfig={{ bottom: i === FOOTER_SKELETON_COUNT - 1 }} />
              </Fragment>
            ))}
          </View>
        ) : null
      }
      style={[boxShadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS), backgroundColor: C('Background/Light/System') }, containerStyle]}
    />
  );
};

export default ColorWrapper(DashboardList);
